import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { paymentsRepo } from '../repo/paymentsRepo'
import { logAudit } from '@/lib/audit'

export function usePayments() {
  return useQuery({
    queryKey: ['payments'],
    queryFn: () => paymentsRepo.findAll(),
  })
}

export function useClientPayments(clientId: string | undefined) {
  return useQuery({
    queryKey: ['payments', 'client', clientId],
    queryFn: () => clientId ? paymentsRepo.findByClient(clientId) : [],
    enabled: !!clientId,
  })
}

export function useCreatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payment: any) => paymentsRepo.create(payment),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['payments', 'client', data.client_id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      logAudit({
        action: 'create_payment',
        resource_type: 'payment',
        resource_id: data.id,
        client_id: data.client_id,
      })
    },
    onError: (error: any) => {
      console.error('Error creating payment:', error)
      alert(`Failed to create payment: ${error.message || 'Unknown error'}`)
    },
  })
}

export function useUpdatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      paymentsRepo.update(id, updates),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['payments', 'client', data.client_id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      logAudit({
        action: 'update_payment',
        resource_type: 'payment',
        resource_id: data.id,
        client_id: data.client_id,
      })
    },
  })
}

export function useDeletePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => paymentsRepo.delete(id),
    onSuccess: (_data, id) => {
      console.log('Payment deleted successfully, invalidating queries')

      // Invalidate all payment-related queries
      queryClient.invalidateQueries({ queryKey: ['payments'] })

      // Invalidate all invoice-related queries
      queryClient.invalidateQueries({ queryKey: ['invoices'] })

      // Invalidate customer queries (for customer detail page)
      queryClient.invalidateQueries({ queryKey: ['customers'] })

      // Invalidate dashboard stats
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })

      // Invalidate all client-specific queries
      queryClient.invalidateQueries({ queryKey: ['clients'] })

      logAudit({
        action: 'delete_payment',
        resource_type: 'payment',
        resource_id: id,
      })
    },
    onError: (error: any) => {
      console.error('Error deleting payment:', error)
      alert(`Failed to delete payment: ${error.message || 'Unknown error'}`)
    },
  })
}
